// Orden de Servicio del CMMS. Una sola plantilla que se adapta:
// - con membrete del cliente (o de la empresa) como fondo de hoja completa;
//   sin membrete, encabezado con logo y datos de la empresa;
// - con rutina (checklist + mediciones) o sin ella (orden simple);
// - (etapa 4) con la leyenda y el numero del contrato.
// datos: respuesta de cmms/datosOrdenServicioPdfCMMS.
//
// Trampas de pdfmake 0.1.71 cuidadas: ninguna tabla con body vacio, cada fila con
// tantas celdas como widths, widths nuevos en cada tabla, texto largo partible.
import { lineasFirma, aplicarFondo } from "./comunCMMSPdf";

const AZUL = "#1f3b57";
const GRIS = "#5b6b7c";
const LINEA = "#9aa7b4";

const partible = (t) => String(t ?? "").replace(/(\S{18})/g, "$1​");

const fecha = (f, conHora) => {
    if (!f) return "";
    const d = new Date(f);
    if (isNaN(d.getTime())) return "";
    return conHora
        ? d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

const hora = (f) => {
    if (!f) return "";
    const d = new Date(f);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
};

const bordes = {
    hLineWidth: () => 0.6,
    vLineWidth: () => 0.6,
    hLineColor: () => LINEA,
    vLineColor: () => LINEA,
    paddingLeft: () => 5,
    paddingRight: () => 5,
    paddingTop: () => 3,
    paddingBottom: () => 3,
};

const titulo = (texto) => ({ text: texto, bold: true, color: AZUL, fontSize: 9.5, margin: [0, 8, 0, 3] });

const dato = (etiqueta, valor) => ({ text: [{ text: `${etiqueta}: `, bold: true }, partible(valor || "")], margin: [0, 0, 0, 1.5] });

const caja = (texto, minAlto = 30) => ({
    table: { widths: ["*"], heights: [minAlto], body: [[{ text: partible(texto || " "), fontSize: 8.5 }]] },
    layout: bordes,
});

const casilla = (marcada) => ({
    table: { widths: [8], heights: [8], body: [[{ text: marcada ? "X" : " ", fontSize: 7, bold: true, alignment: "center", margin: [0, -1.5, 0, 0] }]] },
    layout: { hLineWidth: () => 0.8, vLineWidth: () => 0.8, hLineColor: () => "#444444", vLineColor: () => "#444444", paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
});

// Paso que no aplica a este equipo: "N/A" en lugar de la casilla.
const marcaNoAplica = () => ({ text: "N/A", fontSize: 6.5, bold: true, color: GRIS, alignment: "center" });

const vacio = (v) => v === null || v === undefined || v === "";

// Limites inferior/superior de la medicion; en modo tolerancia se calculan del esperado
// (igual que Utils/cmms/mediciones.utils.js del backend) y se devuelve la nota "120 ± 10 %".
const rangoTexto = (a) => {
    const n = (v) => (vacio(v) ? "" : `${Math.round(Number(v) * 1000) / 1000}${a.unidad ? " " + a.unidad : ""}`);
    if (a.modoRango === "tolerancia" && !vacio(a.valorEsperado)) {
        const esperado = Number(a.valorEsperado);
        const t = vacio(a.tolerancia) ? 0 : Number(a.tolerancia);
        const delta = a.toleranciaTipo === "abs" ? t : Math.abs(esperado) * (t / 100);
        const nota = `${n(esperado)} ± ${t}${a.toleranciaTipo === "abs" ? (a.unidad ? " " + a.unidad : "") : " %"}`;
        return [n(esperado - delta), n(esperado + delta), nota];
    }
    return [n(a.limiteInferior), n(a.limiteSuperior), ""];
};

const seccionChecklist = (s) => {
    const filas = (s.actividades || []).map((a, i) => [
        { text: `${i + 1}.`, color: GRIS, fontSize: 8 },
        {
            text: [{ text: a.titulo, bold: true }, a.descripcion ? ` ${a.descripcion}` : "", a.observacion ? { text: `  — ${a.observacion}`, italics: true, color: GRIS } : ""],
            fontSize: 8.5,
            color: a.noAplica ? GRIS : undefined,
        },
        a.noAplica ? marcaNoAplica() : { ...casilla(!!a.realizado), margin: [3, 0, 0, 0] },
    ]);
    if (!filas.length) return [];
    return [
        titulo(s.titulo),
        { table: { widths: [14, "*", 14], body: filas }, layout: { ...bordes, hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0 : 0.3), vLineWidth: () => 0 } },
    ];
};

const seccionMediciones = (s) => {
    const encabezado = [
        { text: "Prueba", bold: true, fillColor: "#eef2f6" },
        { text: "Límite inferior", bold: true, fillColor: "#eef2f6", alignment: "center" },
        { text: "Límite superior", bold: true, fillColor: "#eef2f6", alignment: "center" },
        { text: "Valor medido", bold: true, fillColor: "#eef2f6", alignment: "center" },
        { text: "Resultado", bold: true, fillColor: "#eef2f6", alignment: "center" },
    ];
    const filas = [encabezado];
    const grupos = [];
    (s.actividades || []).forEach((a) => {
        const [r1, r2, nota] = rangoTexto(a);
        const valor = vacio(a.valorObtenido) || a.noAplica ? "" : `${a.valorObtenido}${a.unidad ? " " + a.unidad : ""}`;
        const resultado = a.noAplica
            ? { text: "N/A", color: GRIS, bold: true }
            : a.dentroDeRango === true
            ? { text: "Dentro", color: "#2e7d32", bold: true }
            : a.dentroDeRango === false
            ? { text: "Fuera", color: "#c62828", bold: true }
            : { text: "" };
        filas.push([
            { text: [{ text: a.titulo, bold: true }, a.condicion ? ` ${a.condicion}` : "", nota ? { text: `  (${nota})`, color: GRIS } : ""] },
            { text: r1, alignment: "center" },
            { text: r2, alignment: "center" },
            { text: valor, alignment: "center" },
            { ...resultado, alignment: "center" },
        ]);
        if (a.grupo && !grupos.includes(a.grupo)) grupos.push(a.grupo);
    });
    const obs = (s.observacionesGrupo || []).filter((o) => o.texto);
    const bloque = [titulo(s.titulo), { table: { headerRows: 1, widths: ["*", 62, 62, 62, 50], body: filas }, layout: bordes, fontSize: 8 }];
    if (obs.length) {
        bloque.push({
            table: { widths: [110, "*"], body: obs.map((o) => [{ text: `Observaciones · ${o.grupo}`, bold: true, fontSize: 8 }, { text: partible(o.texto), fontSize: 8 }]) },
            layout: bordes,
            margin: [0, 3, 0, 0],
        });
    }
    return bloque;
};

const encabezadoSinMembrete = (datos) => {
    const e = datos.empresa || {};
    return {
        columns: [
            e.logo ? { image: "logoEmpresa", fit: [90, 55], width: 100 } : { text: "", width: 0 },
            {
                stack: [
                    { text: e.nombre || "", bold: true, fontSize: 11, color: AZUL },
                    e.rfc ? { text: `R.F.C. ${e.rfc}`, fontSize: 8, color: GRIS } : "",
                    e.direccion ? { text: partible(e.direccion), fontSize: 8, color: GRIS } : "",
                    { text: [e.telefono ? `Tel. ${e.telefono}` : "", e.telefono && e.correo ? " · " : "", e.correo || ""].join(""), fontSize: 8, color: GRIS },
                ].filter(Boolean),
                margin: [8, 2, 0, 0],
            },
        ],
        margin: [0, 0, 0, 6],
    };
};

const ordenServicioCMMSPdf = (datos) => {
    const s = datos.servicio || {};
    const imp = datos.impresion || {};
    const c = datos.cliente;
    const a = s.activo;
    const conMembrete = !!imp.membrete;
    const m = imp.margenes || {};
    const content = [];

    if (!conMembrete) content.push(encabezadoSinMembrete(datos));

    // Titulo + caja Fecha / Folio / Tipo
    content.push({
        columns: [
            { text: "Orden de Servicio", fontSize: 16, bold: true, color: AZUL, margin: [0, 6, 0, 0] },
            {
                width: 200,
                table: {
                    widths: [62, "*"],
                    body: [
                        [{ text: "Fecha:", bold: true }, fecha(s.fecha, true)],
                        [{ text: "Folio:", bold: true }, partible(s.folioExterno ? `${s.folioExterno} (${s.codigo})` : s.codigo)],
                        [{ text: "Tipo de servicio:", bold: true }, s.tipoServicioNombre || ""],
                    ],
                },
                layout: bordes,
                fontSize: 8.5,
            },
        ],
    });
    if (s.contrato?.leyenda) content.push({ text: partible(s.contrato.leyenda), fontSize: 8, bold: true, alignment: "right", margin: [0, 4, 0, 0] });
    if (s.contrato?.numero)
        content.push({ text: `Contrato: ${s.contrato.numero}`, fontSize: 8, bold: true, alignment: "right", margin: [0, s.contrato.leyenda ? 0 : 4, 0, 0] });

    // Datos del cliente / del equipo
    const colCliente = c
        ? [
              dato("Cliente", c.nombre),
              c.sucursal ? dato("Sucursal", c.sucursal) : "",
              imp.mostrarRfc !== false ? dato("RFC", c.rfc) : "",
              dato("Responsable", s.responsableCliente),
              dato("Dirección", c.direccion),
              imp.mostrarTelefono !== false ? dato("Teléfono", c.telefono) : "",
              imp.mostrarCorreo !== false ? dato("Correo", c.correo) : "",
          ]
        : [dato("Cliente", "—"), dato("Responsable", s.responsableCliente)];
    const colEquipo = a
        ? [
              dato("Código de inventario", a.numeroInventario || a.codigo),
              dato("Tipo de equipo", a.tipo),
              dato("Área", [s.ubicacion, s.area].filter(Boolean).join(" · ")),
              dato("Marca", a.marca),
              dato("Modelo", a.modelo),
              dato("Número de serie", a.numeroSerie),
              a.idExterno ? dato("ID", a.idExterno) : "",
          ]
        : [{ text: "Sin equipo", italics: true, color: GRIS }];
    content.push({
        table: {
            widths: ["*", "*"],
            body: [
                [
                    { text: "Datos del cliente", bold: true, color: AZUL, fillColor: "#eef2f6" },
                    { text: "Datos del equipo", bold: true, color: AZUL, fillColor: "#eef2f6" },
                ],
                [{ stack: colCliente.filter(Boolean), fontSize: 8.5 }, { stack: colEquipo.filter(Boolean), fontSize: 8.5 }],
            ],
        },
        layout: bordes,
        margin: [0, 10, 0, 0],
    });

    // Lugar · condiciones · horas
    content.push({
        table: {
            widths: ["*", "*", "*"],
            body: [
                [
                    { text: "Lugar", bold: true, color: AZUL, fillColor: "#eef2f6" },
                    { text: "Condiciones de recepción del equipo", bold: true, color: AZUL, fillColor: "#eef2f6" },
                    { text: "Condiciones de entrega al terminar", bold: true, color: AZUL, fillColor: "#eef2f6" },
                ],
                [partible(s.lugar || ""), partible(s.condicionRecepcion || ""), partible(s.condicionEntrega || "")],
                [
                    { text: [{ text: "Ingeniero: ", bold: true }, s.ingeniero || ""] },
                    { text: [{ text: "Hora de inicio: ", bold: true }, hora(s.fechaInicio)] },
                    { text: [{ text: "Hora de finalización: ", bold: true }, hora(s.fechaConclusion)] },
                ],
            ],
        },
        layout: bordes,
        fontSize: 8.5,
        margin: [0, 6, 0, 0],
    });

    const conRutina = !!(s.rutina && (s.rutina.secciones || []).some((x) => (x.actividades || []).length));
    const bloques = [
        ["Falla reportada", s.fallaReportada],
        ["Falla encontrada", s.fallaEncontrada],
        ["Reporte técnico", s.reporteTecnico],
        ["Notas y recomendaciones", s.notas],
    ];
    bloques.forEach(([t, v]) => {
        // En la orden con rutina se omiten los vacios para no alargarla.
        if (conRutina && !v) return;
        content.push(titulo(t), caja(v, t === "Reporte técnico" ? 40 : 24));
    });

    if (conRutina) {
        content.push({ text: s.rutina.titulo || "Rutina de mantenimiento", bold: true, fontSize: 10.5, color: AZUL, margin: [0, 12, 0, 0] });
        (s.rutina.secciones || []).forEach((sec) => {
            content.push(...(sec.tipo === "mediciones" ? seccionMediciones(sec) : seccionChecklist(sec)));
        });
    }

    // Refacciones
    const partes = s.partes || [];
    content.push(titulo("Refacciones o consumibles"));
    content.push({
        table: {
            headerRows: 1,
            widths: [45, "*", "*"],
            body: [
                [
                    { text: "Cantidad", bold: true, fillColor: "#eef2f6", alignment: "center" },
                    { text: "Refacción o consumible", bold: true, fillColor: "#eef2f6" },
                    { text: "Motivo de sustitución", bold: true, fillColor: "#eef2f6" },
                ],
                ...(partes.length
                    ? partes.map((p) => [{ text: String(p.cantidad ?? ""), alignment: "center" }, partible(p.descripcion), partible(p.motivoSustitucion)])
                    : [[{ text: " " }, { text: "Sin refacciones", color: GRIS, italics: true }, { text: " " }]]),
            ],
        },
        layout: bordes,
        fontSize: 8.5,
    });

    // Evidencias
    const evidencias = datos.evidencias || [];
    if (evidencias.length) {
        content.push(titulo("Evidencias"));
        const filas = [];
        for (let i = 0; i < evidencias.length; i += 3) {
            const fila = [0, 1, 2].map((k) => {
                const ev = evidencias[i + k];
                return ev
                    ? { stack: [{ image: ev.imagen, fit: [160, 120], alignment: "center" }, { text: [ev.etiqueta, ev.nombre].filter(Boolean).join(" · "), fontSize: 7, color: GRIS, alignment: "center", margin: [0, 2, 0, 0] }] }
                    : { text: "" };
            });
            filas.push(fila);
        }
        content.push({ table: { widths: ["*", "*", "*"], body: filas, dontBreakRows: true }, layout: "noBorders" });
    }

    // Firmas: la linea mide lo que su columna y el nombre va centrado debajo.
    // Con firma de la app se dibuja sobre la linea (y su nombre si lo capturo).
    const firma = (rol) => (s.firmas || []).find((f) => f.rol === rol);
    const firmas = [
        { etiqueta: "Ingeniero de servicio", nombre: firma("ingeniero")?.nombre || s.ingeniero, imagen: firma("ingeniero")?.imagen },
        { etiqueta: "Responsable área usuaria", nombre: firma("cliente")?.nombre || s.responsableCliente, imagen: firma("cliente")?.imagen },
    ];
    if (imp.lineasFirma === 3 || firma("biomedica"))
        firmas.push({ etiqueta: "Responsable de Biomédica / Mantenimiento", nombre: firma("biomedica")?.nombre || "", imagen: firma("biomedica")?.imagen });
    content.push(lineasFirma(firmas, { margenArriba: 38, tamEtiqueta: 8 }));

    const pie = [imp.textoPie, datos.proveedorServicio ? `Proveedor de servicio: ${datos.proveedorServicio}` : ""].filter(Boolean).join(" · ");
    const dd = {
        pageSize: "LETTER",
        pageMargins: conMembrete ? [m.izquierda ?? 40, m.arriba ?? 110, m.derecha ?? 40, m.abajo ?? 90] : [36, 32, 36, 46],
        content,
        defaultStyle: { fontSize: 9, color: "#1f2a37" },
        images: {},
        footer: (actual, total) => ({
            columns: [
                { text: conMembrete ? "" : pie, fontSize: 7, color: GRIS, margin: [36, 10, 0, 0] },
                { text: `${s.codigo || ""} · Página ${actual} de ${total}`, alignment: "right", fontSize: 7, color: GRIS, margin: [0, 10, 36, 0] },
            ],
        }),
    };
    // Membrete de hoja completa y marca de agua: del cliente o, si no tiene, de la empresa.
    aplicarFondo(dd, imp);
    if (!conMembrete && datos.empresa?.logo) dd.images.logoEmpresa = datos.empresa.logo;
    return dd;
};

export default ordenServicioCMMSPdf;
